/**
 * Effect Catalog
 * SmartAssetResolver integration for effect asset loading
 */

import { SmartAssetResolver } from '@/core/index.js';
import { logger } from '@/utils/logger.js';
import type { EffectCatalog } from './types.js';
import { useTokchanStore } from '@/store/tokchanStore.js';

/**
 * Asset resolver interface
 */
interface AssetResolver {
  resolveAsset(category: string, name: string, format: string): Promise<{
    formats?: {
      webm?: { path: string };
      mp4?: { path: string };
    };
  } | null>;
}

/**
 * Resolve effect paths from catalog
 */
export async function resolveEffectPaths(effectList: unknown[], resolver: AssetResolver): Promise<string[]> {
  const resolvedPaths: string[] = [];

  for (const effect of effectList) {
    if (!effect || typeof effect !== 'object') continue;

    const effectObj = effect as { category?: string; name?: string };
    if (!effectObj.category || !effectObj.name) continue;

    try {
      const resolvedEffect = await resolver.resolveAsset(effectObj.category, effectObj.name, 'webm');
      if (resolvedEffect && resolvedEffect.formats) {
        // WebM preferred, MP4 fallback
        if (resolvedEffect.formats.webm) {
          resolvedPaths.push(`/assets/effect/${resolvedEffect.formats.webm.path}`);
        } else if (resolvedEffect.formats.mp4) {
          resolvedPaths.push(`/assets/effect/${resolvedEffect.formats.mp4.path}`);
        }
      }
    } catch (error) {
      logger.warn(`🎆 Failed to resolve effect: ${effectObj.category}/${effectObj.name}`, error);
    }
  }

  return resolvedPaths;
}

/**
 * Initialize effect catalog with SmartAssetResolver
 */
export async function initializeEffectCatalog(effectCatalog: EffectCatalog): Promise<void> {
  const tryInitialize = async () => {
    // Zustand store優先
    const mediaPathRegistry = useTokchanStore.getState().getMediaPathRegistry();
    if (mediaPathRegistry) {
      try {
        const resolver = new SmartAssetResolver();

        // Kick beat effects (explosion, fire, flash)
        effectCatalog.kick = await resolveEffectPaths([
          { category: 'explosion', name: 'Explosion 004 Radial' },
          { category: 'fire', name: 'Fire 033 Impact Explosion Radial' },
          { category: 'flash', name: 'Flash 009 Impact Explosion Radial' },
          { category: 'energy', name: 'Energy 15' }
        ], resolver);

        // Snare beat effects (electricity, smoke, lines)
        effectCatalog.snare = await resolveEffectPaths([
          { category: 'electricity', name: 'Electric discharge 01' },
          { category: 'smoke', name: 'Smoke 002 Impact Explosion Radial' },
          { category: 'shape_lines', name: 'Shape Lines 15' }
        ], resolver);

        // Hihat beat effects (basic shapes, subtle effects)
        effectCatalog.hihat = await resolveEffectPaths([
          { category: 'basic_shape', name: 'Basic Shape 01' }
        ], resolver);

        // Add transitions
        if (mediaPathRegistry.getTransitionPath) {
          const transitionPath = mediaPathRegistry.getTransitionPath(15);
          if (transitionPath) {
            effectCatalog.hihat.push(transitionPath);
          }
        }
      } catch (error) {
        logger.warn('🎆 VisualBeatEffects: SmartAssetResolver initialization failed:', error);
        useFallbackCatalog(effectCatalog);
      }
    } else {
      // Retry later
      setTimeout(tryInitialize, 1000);
    }
  };

  tryInitialize();
}

/**
 * Use fallback catalog (minimal effects)
 */
export function useFallbackCatalog(effectCatalog: EffectCatalog): void {
  logger.warn('🎆 VisualBeatEffects: Using fallback catalog (limited effects)');
  effectCatalog.kick = [];
  effectCatalog.snare = [];
  effectCatalog.hihat = [];
}
